/**
 * State machine for the three-step create-alias wizard: username, display name,
 * then password.
 *
 * Two rules shape this hook:
 *
 * - **Free aliases do not choose a username.** They are handed one from the
 *   shared generator and may rotate it as often as they like. Generation is
 *   local, so rotation costs nothing; only advancing a step talks to the server.
 *   The display name is open to every tier.
 * - **Nothing here is a security boundary.** The server re-checks the character
 *   set, the blocked-word list, the generated-name requirement, and availability
 *   on both `name-check` and create. This hook exists so the user finds out
 *   before typing a password, not to enforce anything.
 *
 * @module hooks/useCreateAliasWizard
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_MIN_LENGTH,
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
  checkUsernameShape,
  createApiClient,
  formatUsernameAsDisplayName,
  generateUsername,
  hasPaidAccess,
  normalizeUsername,
  type NameCheckRejectionCode,
  type ProfileVisibility,
} from '@adieuu/shared';
import { useAppConfig } from '../config';
import { useAuth } from './useAuth';

/** Wizard steps, in order. */
export const CREATE_ALIAS_STEPS = ['username', 'displayName', 'password'] as const;
export type CreateAliasStep = (typeof CREATE_ALIAS_STEPS)[number];

export interface CreateAliasWizardState {
  step: number;
  /** Whether the alias may pick its own username, or must use a generated one. */
  canCustomizeUsername: boolean;

  username: string;
  setUsername: (value: string) => void;
  /** Replaces the username with a freshly generated one. Free tier only. */
  rotateUsername: () => void;

  displayName: string;
  setDisplayName: (value: string) => void;
  displayNamePrivacy: ProfileVisibility;
  setDisplayNamePrivacy: (value: ProfileVisibility) => void;

  passphrase: string;
  setPassphrase: (value: string) => void;
  passphraseConfirm: string;
  setPassphraseConfirm: (value: string) => void;

  /** True while a `name-check` request for the current step is outstanding. */
  checking: boolean;
  /** Server verdict for the current step's field, or `null` when it passed. */
  rejection: NameCheckRejectionCode | null;
  /** Set when the check itself failed (offline, 401, 429) rather than the name. */
  checkFailed: boolean;

  /** Whether the current step's field is locally well-formed enough to submit. */
  canAdvance: boolean;

  /**
   * Runs the server check for the current step and advances on success.
   * Resolves `true` when the step advanced.
   */
  advance: () => Promise<boolean>;
  back: () => void;
  reset: () => void;
}

/**
 * Local shape check for the current step, so the Next button is disabled before
 * a doomed request is made. Deliberately looser than the server: it never
 * decides a name is *acceptable*, only that it is worth asking about.
 */
function isLocallyValid(
  step: number,
  username: string,
  displayName: string,
  canCustomizeUsername: boolean,
): boolean {
  if (step === 0) return checkUsernameShape(normalizeUsername(username), canCustomizeUsername).ok;
  if (step === 1) {
    const trimmed = displayName.trim();
    return trimmed.length >= DISPLAY_NAME_MIN_LENGTH && trimmed.length <= DISPLAY_NAME_MAX_LENGTH;
  }
  return true;
}

export function useCreateAliasWizard(): CreateAliasWizardState {
  const { session } = useAuth();
  const { apiBaseUrl } = useAppConfig();
  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);

  const canCustomizeUsername = useMemo(
    () =>
      hasPaidAccess({
        subscriptions: session?.subscriptions,
        entitlements: session?.entitlements,
        isLifetime: session?.isLifetime,
      }),
    [session?.subscriptions, session?.entitlements, session?.isLifetime],
  );

  const [step, setStep] = useState(0);
  const [username, setUsernameRaw] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [displayNamePrivacy, setDisplayNamePrivacy] = useState<ProfileVisibility>('public');
  const [passphrase, setPassphrase] = useState('');
  const [passphraseConfirm, setPassphraseConfirm] = useState('');
  const [checking, setChecking] = useState(false);
  const [rejection, setRejection] = useState<NameCheckRejectionCode | null>(null);
  const [checkFailed, setCheckFailed] = useState(false);

  /** Guards against a late response from a superseded check overwriting state. */
  const checkSeq = useRef(0);
  /**
   * Once the user edits the display name, username changes (rotation, typing)
   * must not overwrite it. Until then, advancing off the username step reseeds
   * from the current username so a free-tier rotation lands as a fresh suggestion.
   */
  const displayNameTouched = useRef(false);

  // Seed the free tier with a generated name so the first step is never blank.
  useEffect(() => {
    if (!canCustomizeUsername && username === '') {
      setUsernameRaw(generateUsername());
    }
  }, [canCustomizeUsername, username]);

  const setUsername = useCallback((value: string) => {
    // Usernames are always lowercase, so normalize as the user types rather than
    // surprising them by rewriting the value on submit.
    setUsernameRaw(normalizeUsername(value).slice(0, USERNAME_MAX_LENGTH));
    setRejection(null);
    setCheckFailed(false);
  }, []);

  const rotateUsername = useCallback(() => {
    setUsernameRaw(generateUsername());
    setRejection(null);
    setCheckFailed(false);
  }, []);

  const setDisplayNameChecked = useCallback((value: string) => {
    displayNameTouched.current = true;
    setDisplayName(value.slice(0, DISPLAY_NAME_MAX_LENGTH));
    setRejection(null);
    setCheckFailed(false);
  }, []);

  const advance = useCallback(async (): Promise<boolean> => {
    // The password step has nothing to ask the server about; the modal submits it.
    if (step >= CREATE_ALIAS_STEPS.length - 1) return false;

    const signedToken = session?.signedToken;
    if (!signedToken) {
      setCheckFailed(true);
      return false;
    }

    const seq = ++checkSeq.current;
    setChecking(true);
    setRejection(null);
    setCheckFailed(false);

    try {
      const response = await api.identity.checkName(
        step === 0
          ? { signedToken, username: normalizeUsername(username) }
          : { signedToken, displayName: displayName.trim() },
      );

      if (seq !== checkSeq.current) return false;

      const verdict = step === 0 ? response.data?.username : response.data?.displayName;
      if (!response.success || !verdict) {
        setCheckFailed(true);
        return false;
      }

      if (!verdict.ok) {
        setRejection(verdict.code);
        return false;
      }

      // Prefill from the username the first time (and after any rotation that
      // happened before the user typed). Generated names become "Adjective Noun 42".
      if (step === 0 && !displayNameTouched.current) {
        setDisplayName(
          formatUsernameAsDisplayName(normalizeUsername(username)).slice(
            0,
            DISPLAY_NAME_MAX_LENGTH,
          ),
        );
      }

      setStep((current) => current + 1);
      return true;
    } catch {
      if (seq === checkSeq.current) setCheckFailed(true);
      return false;
    } finally {
      if (seq === checkSeq.current) setChecking(false);
    }
  }, [api, displayName, session?.signedToken, step, username]);

  const back = useCallback(() => {
    // Abandons any in-flight check so its verdict cannot land on the new step.
    checkSeq.current += 1;
    setChecking(false);
    setRejection(null);
    setCheckFailed(false);
    setStep((current) => Math.max(0, current - 1));
  }, []);

  const reset = useCallback(() => {
    checkSeq.current += 1;
    displayNameTouched.current = false;
    setStep(0);
    setUsernameRaw('');
    setDisplayName('');
    setDisplayNamePrivacy('public');
    setPassphrase('');
    setPassphraseConfirm('');
    setChecking(false);
    setRejection(null);
    setCheckFailed(false);
  }, []);

  return {
    step,
    canCustomizeUsername,
    username,
    setUsername,
    rotateUsername,
    displayName,
    setDisplayName: setDisplayNameChecked,
    displayNamePrivacy,
    setDisplayNamePrivacy,
    passphrase,
    setPassphrase,
    passphraseConfirm,
    setPassphraseConfirm,
    checking,
    rejection,
    checkFailed,
    canAdvance: !checking && isLocallyValid(step, username, displayName, canCustomizeUsername),
    advance,
    back,
    reset,
  };
}

export { USERNAME_MIN_LENGTH, USERNAME_MAX_LENGTH, DISPLAY_NAME_MAX_LENGTH };
